import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Sidebar from './Sidebar'
import ArticleView from './ArticleView'
import SearchResults from './SearchResults'
import { ArticleSkeleton, SidebarSkeleton } from './Skeleton'
import { loadIndex, loadArticleBody, searchArticles } from './contentIndex'

const HASH_PREFIX = '#help/'
const DEFAULT_SLUG = 'prise-en-main/bienvenue'
const FALLBACK_LOCALE = 'fr'

const detectLocale = explicit => {
  if (explicit) return explicit
  if (typeof window !== 'undefined' && window.I18n && window.I18n.locale) return window.I18n.locale
  return FALLBACK_LOCALE
}

const readSlugFromHash = () => {
  const h = (window.location.hash || '').replace(/^#/, '')
  if (h.startsWith('help/')) return h.slice('help/'.length)
  return null
}

const writeSlugToHash = slug => {
  if (slug) window.history.replaceState(null, '', `${HASH_PREFIX}${slug}`)
  else if ((window.location.hash || '').startsWith(HASH_PREFIX)) window.history.replaceState(null, '', window.location.pathname + window.location.search)
}

const HelpCenterApp = ({ tooltip = 'Aide', initialSlug = null, locale = null }) => {
  const activeLocale = useMemo(() => detectLocale(locale), [locale])

  const [index, setIndex] = useState(null) // { tree, articles } once loaded
  const [indexError, setIndexError] = useState(null)
  const [bodies, setBodies] = useState({}) // slug -> markdown body
  const [bodyError, setBodyError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [currentSlug, setCurrentSlug] = useState(initialSlug || DEFAULT_SLUG)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState({})

  const tree = index ? index.tree : []
  const articles = index ? index.articles : {}
  const chaptersByKey = useMemo(() => Object.fromEntries(tree.map(c => [c.key, c])), [tree])

  // Lazy-load the index on first open. Cached at module level so reopening
  // the modal during the same page is instant.
  useEffect(() => {
    if (!isOpen || index) return undefined
    let cancelled = false
    loadIndex(activeLocale)
      .then(data => {
        if (cancelled) return
        setIndex(data)
        setExpanded(Object.fromEntries(data.tree.map(c => [c.key, true])))
      })
      .catch(err => { if (!cancelled) setIndexError(err) })
    return () => { cancelled = true }
  }, [isOpen, index, activeLocale])

  // Open the modal automatically if landing with #help/... in the URL
  useEffect(() => {
    const slug = readSlugFromHash()
    if (slug) {
      setCurrentSlug(slug)
      setIsOpen(true)
    }
  }, [])

  // React to back/forward navigation
  useEffect(() => {
    const onHashChange = () => {
      const slug = readSlugFromHash()
      if (slug) {
        setCurrentSlug(slug)
        setIsOpen(true)
      }
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    writeSlugToHash(null)
  }, [])

  // Close on Escape, prevent body scroll while open
  useEffect(() => {
    if (!isOpen) return undefined
    const onKey = e => { if (e.key === 'Escape') close() }
    document.body.classList.add('hc-no-scroll')
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('hc-no-scroll')
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen, close])

  const open = useCallback(() => setIsOpen(true), [])

  const selectArticle = useCallback(slug => {
    setCurrentSlug(slug)
    setQuery('')
    writeSlugToHash(slug)
    const chapterKey = slug.split('/')[0]
    setExpanded(prev => ({ ...prev, [chapterKey]: true }))
  }, [])

  const selectChapter = useCallback(chapterKey => {
    const chapter = chaptersByKey[chapterKey]
    if (!chapter) return
    if (chapter.intro) {
      setCurrentSlug(`${chapterKey}`)
    } else if (chapter.articles.length) {
      selectArticle(chapter.articles[0].slug)
    }
  }, [chaptersByKey, selectArticle])

  const toggleChapter = useCallback(key => {
    setExpanded(prev => ({ ...prev, [key]: prev[key] === false }))
  }, [])

  const results = useMemo(() => searchArticles(query, articles), [query, articles])

  // Resolve current article — special-case chapter intros (slug equals chapter key, no slash)
  let currentArticle = null
  let currentChapter = null
  let needsBody = null

  if (!currentSlug.includes('/')) {
    const chapter = chaptersByKey[currentSlug]
    if (chapter && chapter.intro) {
      currentArticle = {
        slug:    currentSlug,
        title:   chapter.title,
        intro:   '',
        body:    chapter.intro,
        chapter: currentSlug
      }
      currentChapter = chapter
    }
  } else if (articles[currentSlug]) {
    const meta = articles[currentSlug]
    currentChapter = chaptersByKey[meta.chapter]
    if (bodies[currentSlug] !== undefined) {
      currentArticle = { ...meta, body: bodies[currentSlug] }
    } else {
      needsBody = currentSlug
    }
  }

  // Lazy-fetch the body for the current article
  useEffect(() => {
    if (!needsBody) return undefined
    let cancelled = false
    setBodyError(null)
    loadArticleBody(activeLocale, needsBody)
      .then(body => {
        if (cancelled) return
        setBodies(prev => ({ ...prev, [needsBody]: body }))
      })
      .catch(err => { if (!cancelled) setBodyError(err) })
    return () => { cancelled = true }
  }, [needsBody, activeLocale])

  const renderMain = () => {
    if (indexError) {
      return <p className="hc-article__placeholder">{I18n.t('help_center_modal.index_error')}</p>
    }
    if (!index) return <ArticleSkeleton />
    if (query) {
      return <SearchResults query={query} results={results} onSelect={selectArticle} chaptersByKey={chaptersByKey} />
    }
    if (needsBody && !bodyError) return <ArticleSkeleton />
    if (bodyError) {
      return <p className="hc-article__placeholder">{I18n.t('help_center_modal.article_error')}</p>
    }
    return <ArticleView article={currentArticle} chapter={currentChapter} onInternalLink={selectArticle} />
  }

  return (
    <>
      <button
        type="button"
        className="faq-link hc-trigger"
        aria-label={tooltip}
        onClick={open}
        {...{ tooltip, tooltip_position: 'bottom' }}
      >
        <i className="fa fa-question-circle navbar__icon" aria-hidden="true" />
      </button>

      {isOpen && createPortal(
        <div
          className="hc-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={I18n.t('help_center_modal.dialog_label')}
          onMouseDown={e => { if (e.target.classList.contains('hc-overlay')) close() }}
        >
          <div className="hc-window">
            <header className="hc-toolbar">
              <div className="hc-toolbar__title">
                <i className="fa fa-question-circle" aria-hidden="true" />
                <span>{I18n.t('help_center_modal.title')}</span>
              </div>
              <div className="hc-toolbar__search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={I18n.t('help_center_modal.search_placeholder')}
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    className="hc-toolbar__search-clear"
                    aria-label={I18n.t('help_center_modal.clear_search')}
                    onClick={() => setQuery('')}
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                className="hc-toolbar__close"
                aria-label={I18n.t('help_center_modal.close')}
                onClick={close}
              >
                ×
              </button>
            </header>

            <div className="hc-body">
              {index ? (
                <Sidebar
                  tree={tree}
                  currentSlug={currentSlug}
                  expanded={expanded}
                  onToggleChapter={toggleChapter}
                  onSelect={selectArticle}
                  onSelectChapter={selectChapter}
                />
              ) : (
                <SidebarSkeleton />
              )}
              <main className="hc-main">{renderMain()}</main>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default HelpCenterApp
